/* eslint-disable @typescript-eslint/no-explicit-any */
import BookmarksOutlinedIcon from '@mui/icons-material/BookmarksOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Box, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { RefObject } from 'react';

import { getError } from '@/utils/hookFormUtils';

import AddressInput, { AddressInputRef } from './AddressInput';
import { originAddressForm } from './constants';
import DirectionForm from './DirectionForm';
import InputRef from './InputRef';

interface OriginAddressSectionProps {
  control: any;
  register: any;
  errors: any;
  setValue: any;
  watch: any;
  geoSuggestRef: RefObject<AddressInputRef | null>;
  userCountry?: any;
  showDirectionForm: { inOrigin: boolean; inDestination: boolean };
  showZipCodeInput: { inOrigin: boolean; inDestination: boolean };
  findAddressComponent: any;
  handleZipCodeNotFound: (site: 'inOrigin' | 'inDestination', notFound: boolean) => void;
  handleShowDirectionForm: (site: 'inOrigin' | 'inDestination') => void;
  setProfileData: () => Promise<void>;
  onOpenSavedAddresses?: () => void;
}

const OriginAddressSection = (props: OriginAddressSectionProps) => {
  const {
    control,
    register,
    errors,
    setValue,
    watch,
    geoSuggestRef,
    userCountry,
    showDirectionForm,
    showZipCodeInput,
    findAddressComponent,
    handleZipCodeNotFound,
    handleShowDirectionForm,
    setProfileData,
    onOpenSavedAddresses,
  } = props;

  return (
    <div className="space-y-3">
      <div className="flex flex-row justify-between items-center w-full mt-2 mb-4 px-1 flex-wrap gap-2">
        <FormControlLabel
          control={
            <Checkbox
              checked={showDirectionForm.inOrigin}
              onChange={() => handleShowDirectionForm('inOrigin')}
              name={'origin.showForm'}
              color="primary"
              size={'small'}
              className="!p-1"
            />
          }
          label={'¿No encuentras tu dirección?'}
          sx={{ '&& span': { fontSize: '0.875rem', color: 'text.secondary' } }}
        />

        {onOpenSavedAddresses && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              gap: '0.25rem',
            }}
            onClick={onOpenSavedAddresses}
            width={'fit-content'}
          >
            <BookmarksOutlinedIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Direcciones guardadas
            </Typography>
          </Box>
        )}
      </div>

      {!showDirectionForm.inOrigin && (
        <>
          <AddressInput
            control={control}
            name="origin"
            findAddressComponent={findAddressComponent}
            setValue={setValue}
            ref={geoSuggestRef}
            country={userCountry}
            onZipCodeNotFound={(notFound) => handleZipCodeNotFound('inOrigin', notFound)}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              gap: '0.25rem',
              width: 'fit-content',
              pt: 0.5,
              px: 0.5,
            }}
            onClick={setProfileData}
          >
            <PushPinOutlinedIcon sx={{ fontSize: '0.95rem', color: 'primary.main' }} />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Usa tu dirección principal
            </Typography>
          </Box>
        </>
      )}

      {!showDirectionForm.inOrigin && showZipCodeInput.inOrigin && (
        <Grid size={{ xxs: 12 }} sx={{ mt: 2 }}>
          <TextField
            {...register('origin.zipCode')}
            value={watch('origin.zipCode') || ''}
            size="small"
            variant="outlined"
            fullWidth
            label="Código postal *"
            error={!!getError(errors, 'origin.zipCode')}
            helperText={getError(errors, 'origin.zipCode')?.message}
            slotProps={{
              inputLabel: {
                shrink: Boolean(watch('origin.zipCode')) || undefined,
              },
              htmlInput: {
                autoComplete: 'off',
                role: 'presentation',
                'data-form-type': 'other',
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
              },
            }}
          />
        </Grid>
      )}

      {showDirectionForm.inOrigin && (
        <DirectionForm
          register={register}
          errors={errors}
          directionData={originAddressForm}
          watch={watch}
        />
      )}

      <InputRef errors={errors} register={register} name="origin.reference" watch={watch} />
    </div>
  );
};

export default OriginAddressSection;
